import { ArrowRight, MapPin, Users } from "lucide-react";
import * as React from "react";
import { Button } from "../atoms/button.js";
import { CardContent, CardFooter, CardHeader } from "../atoms/card.js";
import { Input } from "../atoms/input.js";
import { NumberInput } from "../atoms/input.js";
import { Spinner } from "../atoms/spinner.js";
import { Switch } from "../atoms/switch.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";
import { FormActions } from "./form-actions.js";
import { HeadingGroup } from "./heading-group.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip.js";

export type EnrollmentIntakeFormHeaderVariant = "carousel" | "standalone";

export interface EnrollmentIntakeFormValues {
  zipCode: string;
  employeeCount: number;
  offersMedicalBenefits: boolean;
}

export interface EnrollmentIntakeFormTrackingProps {
  form?: string;
  zipInput?: string;
  employeeCountInput?: string;
  medicalSwitch?: string;
  submitButton?: string;
  backButton?: string;
}

export interface EnrollmentIntakeFormProps
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "children" | "title"> {
  values: EnrollmentIntakeFormValues;
  onValuesChange: (patch: Partial<EnrollmentIntakeFormValues>) => void;
  onSubmit: () => void;
  /** Eyebrow above the title (carousel hero). */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  headerVariant?: EnrollmentIntakeFormHeaderVariant;
  /** Shown under ZIP when valid (e.g. resolved state name). */
  zipHelperText?: string;
  zipLabel?: React.ReactNode;
  zipPlaceholder?: string;
  employeeCountLabel?: React.ReactNode;
  employeeCountMin?: number;
  employeeCountMax?: number;
  medicalBenefitsLabel?: React.ReactNode;
  medicalBenefitsDescriptionWhenYes?: React.ReactNode;
  medicalBenefitsDescriptionWhenNo?: React.ReactNode;
  submitLabel?: React.ReactNode;
  submitLoadingLabel?: React.ReactNode;
  isSubmitting?: boolean;
  /** Optional artificial delay before `onSubmit` (ms). Default 0. */
  submitDelayMs?: number;
  submitDisabledHint?: React.ReactNode;
  onBack?: () => void;
  backLabel?: React.ReactNode;
  /** `compact` matches embedded hero carousel density (md inputs, tighter gaps). */
  density?: "default" | "compact";
  /** Optional class on the fields + footer wrapper (e.g. hero carousel bulk enter). */
  fieldsBulkClassName?: string;
  invalidZipMessage?: string;
  invalidEmployeeCountMessage?: string;
  tracking?: EnrollmentIntakeFormTrackingProps;
  zipId?: string;
  employeeCountId?: string;
  medicalBenefitsId?: string;
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

function DisabledSubmitHint({
  disabled,
  message,
  className,
  children,
}: {
  disabled: boolean;
  message: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  if (!disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled
            tabIndex={0}
            aria-label={typeof message === "string" ? message : undefined}
            className={cx("cw-v2-enrollment-intake-form__disabled-hint", className)}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" align="center" className="max-w-xs">
          {message}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * First-step employer intake for benefits enrollment heroes: ZIP, team size,
 * and whether medical coverage is already offered. Renders as a `<form>` with
 * card header/body/footer slots — wrap in `<Card>` at the page level.
 */
export const EnrollmentIntakeForm = React.forwardRef<HTMLFormElement, EnrollmentIntakeFormProps>(
  function EnrollmentIntakeForm(
    {
      backLabel = "Back",
      className,
      density = "default",
      fieldsBulkClassName,
      description,
      employeeCountId = "enrollment-intake-employee-count",
      employeeCountLabel = "How many employees need coverage?",
      employeeCountMax = 50,
      employeeCountMin = 1,
      eyebrow,
      headerVariant = "carousel",
      invalidEmployeeCountMessage = "Enter a number from 1 to 50.",
      invalidZipMessage = "Enter a 5-digit ZIP code.",
      isSubmitting: isSubmittingProp,
      medicalBenefitsDescriptionWhenNo = "Not yet. We are starting fresh.",
      medicalBenefitsDescriptionWhenYes = "Yes. We want to review our options.",
      medicalBenefitsId = "enrollment-intake-medical",
      medicalBenefitsLabel = "Do you offer medical benefits today?",
      onBack,
      onSubmit,
      onValuesChange,
      submitDelayMs = 0,
      submitDisabledHint = "Enter a valid ZIP code and employee count to continue.",
      submitLabel = "Continue",
      submitLoadingLabel = "Finding your path...",
      title,
      tracking,
      values,
      zipHelperText,
      zipId = "enrollment-intake-zip",
      zipLabel = "Business ZIP code",
      zipPlaceholder = "e.g. 94110",
      ...props
    },
    ref,
  ) {
    const [touched, setTouched] = React.useState(false);
    const [isSubmittingInternal, setIsSubmittingInternal] = React.useState(false);
    const submitTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    const isSubmitting = isSubmittingProp ?? isSubmittingInternal;
    const compact = density === "compact";
    const inputSize = compact ? "md" : "lg";

    const zipValid = /^\d{5}$/.test(values.zipCode);
    const employeeValid =
      Number.isInteger(values.employeeCount) &&
      values.employeeCount >= employeeCountMin &&
      values.employeeCount <= employeeCountMax;
    const canSubmit = zipValid && employeeValid;

    React.useEffect(
      () => () => {
        if (submitTimerRef.current) clearTimeout(submitTimerRef.current);
      },
      [],
    );

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit || isSubmitting) {
        setTouched(true);
        return;
      }
      if (submitDelayMs <= 0) {
        onSubmit();
        return;
      }
      setIsSubmittingInternal(true);
      submitTimerRef.current = setTimeout(() => {
        setIsSubmittingInternal(false);
        onSubmit();
      }, submitDelayMs);
    };

    const fieldsAndFooter = (
      <>
        <CardContent
          className={cx(
            "cw-v2-enrollment-intake-form__body",
            compact && "cw-v2-enrollment-intake-form__body--compact",
          )}
        >
          <Input
            id={zipId}
            label={zipLabel}
            placeholder={zipPlaceholder}
            inputMode="numeric"
            value={values.zipCode}
            onChange={(event) =>
              onValuesChange({ zipCode: digitsOnly(event.target.value).slice(0, 5) })
            }
            prefix={<MapPin className="h-4 w-4" aria-hidden />}
            size={inputSize}
            data-track={tracking?.zipInput}
            error={touched && !zipValid ? invalidZipMessage : undefined}
            helperText={zipHelperText}
          />
          <NumberInput
            id={employeeCountId}
            label={employeeCountLabel}
            min={employeeCountMin}
            max={employeeCountMax}
            value={String(values.employeeCount)}
            onValueChange={(next) =>
              onValuesChange({
                employeeCount: Math.min(
                  employeeCountMax,
                  Math.max(employeeCountMin, next ?? employeeCountMin),
                ),
              })
            }
            prefix={<Users className="h-4 w-4" aria-hidden />}
            size={inputSize}
            data-track={tracking?.employeeCountInput}
            error={touched && !employeeValid ? invalidEmployeeCountMessage : undefined}
          />
          <div className="cw-v2-enrollment-intake-form__toggle-row">
            <div className="cw-v2-enrollment-intake-form__toggle-copy">
              <label
                id={`${medicalBenefitsId}-label`}
                htmlFor={medicalBenefitsId}
                className="cw-v2-enrollment-intake-form__toggle-label"
              >
                {medicalBenefitsLabel}
              </label>
              <Typography variant="body" muted>
                {values.offersMedicalBenefits
                  ? medicalBenefitsDescriptionWhenYes
                  : medicalBenefitsDescriptionWhenNo}
              </Typography>
            </div>
            <Switch
              id={medicalBenefitsId}
              aria-labelledby={`${medicalBenefitsId}-label`}
              checked={values.offersMedicalBenefits}
              onCheckedChange={(checked) => onValuesChange({ offersMedicalBenefits: checked })}
              data-track={tracking?.medicalSwitch}
            />
          </div>
        </CardContent>
        <CardFooter>
          <FormActions variant="registration">
            <DisabledSubmitHint
              disabled={!canSubmit}
              message={submitDisabledHint}
              className="w-full"
            >
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={!canSubmit || isSubmitting}
                iconPrefix={isSubmitting ? <Spinner size="sm" /> : undefined}
                iconSuffix={isSubmitting ? undefined : <ArrowRight aria-hidden />}
                className={cx((!canSubmit || isSubmitting) && "pointer-events-none")}
                data-track={tracking?.submitButton}
              >
                {isSubmitting ? submitLoadingLabel : submitLabel}
              </Button>
            </DisabledSubmitHint>
            {onBack ? (
              <Button
                type="button"
                variant="link"
                size="md"
                onClick={onBack}
                data-track={tracking?.backButton}
              >
                {backLabel}
              </Button>
            ) : null}
          </FormActions>
        </CardFooter>
      </>
    );

    return (
      <form
        ref={ref}
        className={cx("cw-v2-enrollment-intake-form", className)}
        onSubmit={handleSubmit}
        data-track={tracking?.form}
        {...props}
      >
        <CardHeader className="cw-v2-enrollment-intake-form__header">
          {headerVariant === "carousel" ? (
            <HeadingGroup
              className="cw-v2-enrollment-intake-form__heading"
              description={description}
              eyebrow={eyebrow}
              title={title}
              variant="card"
              headingLevel={2}
            />
          ) : (
            <div className="cw-v2-enrollment-intake-form__heading-standalone">
              <Typography
                variant="heading-medium"
                as="h2"
                className="text-xl"
                style={{ fontSize: 20 }}
              >
                {title}
              </Typography>
              {description ? (
                <Typography variant="body" muted>
                  {description}
                </Typography>
              ) : null}
            </div>
          )}
        </CardHeader>

        {fieldsBulkClassName ? (
          <div className={fieldsBulkClassName}>{fieldsAndFooter}</div>
        ) : (
          fieldsAndFooter
        )}
      </form>
    );
  },
);
EnrollmentIntakeForm.displayName = "EnrollmentIntakeForm";
